import { KebabHorizontalIcon, PencilIcon, PlusIcon, SyncIcon, TrashIcon } from "@primer/octicons-react";
import { ActionList, ActionMenu, Box, Checkbox, useConfirm } from "@primer/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hocSinhAceChiTietApi } from "../../../../api/hocSinhAceChiTietApi";
import Button from "../../../../components/ui/button";
import DataTable from "../../../../components/ui/data-table";
import { useCommonSelectedHook } from "../../../../hooks/useCommonSelectedHook";
import { ePageBaseStatus } from "../../../../models/ePageBaseStatus";
import { actions } from "../../../../state/actions/actionsWrapper";

import moment from "moment";
import Text from "../../../../components/ui/text";
import { useCommonContext } from "../../../../contexts/common";
import { NotifyHelper } from "../../../../helpers/toast";
import { IHocSinhAceChitiet, IHocSinhAceChitietVM } from "../../../../models/response/hoc-sinh/IHocSinhAceChitiet";
import { IBaseResponeModel } from "../../../../models/response/base-response";
import { RootState } from "../../../../state/reducers";
import { HocSinh } from "../QuanHeGiaDinhHocSinh";
import AnhChiEmEditModal from "./AnhChiEmEditModal";
interface Props {
    hocSinh: HocSinh;
}
const AnhChiEmPage: React.FC<Props> = ({ hocSinh }) => {
    const { isShowEditModal } = useSelector((x: RootState) => x.hocSinhAceChiTiet)
    const { nam_hoc } = useCommonSelectedHook();
    const { translate } = useCommonContext();
    const confirm = useConfirm();
    const dispatch = useDispatch();

    // Local state for data management
    const [hocSinhAceChiTietVms, setHocSinhAceChiTietVms] = useState<IHocSinhAceChitietVM[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const handleReload = useCallback(async () => {
        try {
            setIsLoading(true);
            const res: IBaseResponeModel = await hocSinhAceChiTietApi.select_all_by_hocsinh({
                ts_hocsinh_id: hocSinh.id,
                nam_hoc: nam_hoc
            });
            if (res.is_success) {
                setHocSinhAceChiTietVms(res.data || []);
            } else {
                NotifyHelper.Error(res.message ?? "Có lỗi xảy ra khi tải dữ liệu");
                setHocSinhAceChiTietVms([]);
            }
        } catch (error) {
            console.error(error);
            NotifyHelper.Error("Có lỗi xảy ra khi tải dữ liệu");
            setHocSinhAceChiTietVms([]);
        } finally {
            setIsLoading(false);
        }
    }, [hocSinh.id, nam_hoc])

    // Initial load
    useEffect(() => {
        handleReload();
    }, [handleReload])

    // Auto reload data when modal is opened
    useEffect(() => {
        if (isShowEditModal) {
            handleReload();
        }
    }, [isShowEditModal, handleReload])

    const handleDelete = async (id: number) => {
        if (await confirm({
            content: translate("Base.Label.DeleteConfirm.Content"),
            title: translate("Base.Label.Confirm"),
            cancelButtonContent: translate("Base.Label.Close"),
            confirmButtonContent: translate("Base.Label.Delete"),
            confirmButtonType: "danger"
        })) {
            try {
                const res = await hocSinhAceChiTietApi.deleteNew(id, nam_hoc);
                if (res.is_success) {
                    NotifyHelper.Success("Success");
                    // Reload data after successful delete
                    handleReload();
                } else {
                    NotifyHelper.Error(res.message ?? "");
                }
            } catch (error) {
                NotifyHelper.Error("Có lỗi xảy ra khi xóa");
            }
        }
    }
    const dataSource = useMemo(() => {
        return hocSinhAceChiTietVms.filter(x => x.nam_hoc == nam_hoc)
    }, [hocSinhAceChiTietVms])

    return (
        <Box>
            <DataTable
                data={dataSource}
                isLoading={isLoading}
                title={translate("Anh/chị/em")}
                searchEnable
                emptyComponent={
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" , color:"fg.muted"}}>
                        Không có dữ liệu
                    </Box>
                }
                // paging={{
                //     enable: true,
                //     pageSizeItems: [20, 50, 200, 500, 1000],
                // }}
                actionComponent={
                    <Box sx={{
                        display: "flex"
                    }}>
                        <Button text="Gán anh chị em" leadingVisual={PlusIcon}
                            size="medium"
                            variant="primary"
                            onClick={() => {
                                dispatch(actions.hocSinhAceChiTiet.SHOW_EDIT_MODAL(undefined))
                            }}
                        />
                        <Button text="Base.Label.Refresh" leadingVisual={SyncIcon}
                            size="medium"
                            sx={{ ml: 1 }}
                            onClick={() => {
                                handleReload();
                            }}
                        />
                    </Box>
                }
                columns={[
                    {
                        id: "cmd",
                        caption: "#",
                        width: "50px",
                        align: "center",
                        cellRender: (data: IHocSinhAceChitiet) => {
                            return (
                                <ActionMenu>
                                    <ActionMenu.Button icon={KebabHorizontalIcon} variant="invisible" sx={{ m: -1 }}>&nbsp;</ActionMenu.Button>
                                    <ActionMenu.Overlay width="auto">
                                        <ActionList>
                                            <ActionList.Item onSelect={() => {
                                                dispatch(actions.hocSinhAceChiTiet.SHOW_EDIT_MODAL(data))
                                            }}>
                                                <ActionList.LeadingVisual><PencilIcon /></ActionList.LeadingVisual>
                                                <Text text='Base.Label.Edit' />
                                            </ActionList.Item>
                                            <ActionList.Item variant="danger" onSelect={() => {
                                                handleDelete(data.id)
                                            }}>
                                                <ActionList.LeadingVisual><TrashIcon /></ActionList.LeadingVisual>
                                                <Text text='Base.Label.Delete' />
                                            </ActionList.Item>
                                        </ActionList>
                                    </ActionMenu.Overlay>
                                </ActionMenu>
                            );
                        }
                    },
                    {
                        dataField: "trang_thai",
                        caption: translate("Trạng thái"),
                        width: 200

                    },
                    {
                        dataField: "ma_hs",
                        caption: translate("Mã học sinh"),
                        width: 150

                    },
                    {
                        dataField: "ho_ten",
                        caption: translate("Họ tên"),
                    },

                    {
                        dataField: "ngay_sinh",
                        caption: translate("Ngày sinh"),
                        width: 150,
                        cellRender: (data: IHocSinhAceChitietVM) => {
                            return (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <span style={{ marginLeft: "5px" }}>
                                            {moment(data.ngay_sinh).format("DD/MM/YYYY")}
                                        </span>
                                    </div>
                                </Box>
                            );
                        },
                    },
                    {
                        dataField: "ten_khoi",
                        caption: translate("Khối"),
                    },
                    {
                        dataField: "ten_lop",
                        caption: translate("Lớp"),
                    },
                    {
                        dataField: "is_huonguudai",
                        width: 120,
                        caption: translate("Hưởng ưu đãi"),
                        cellRender: (data: IHocSinhAceChitiet) => {
                            return (
                                <Box sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                }}>
                                    <Checkbox checked={data.is_huonguudai} />
                                </Box>
                            )
                        }
                    },
                    {
                        dataField: "hinh_thuc_mien_giam",
                        width: 200,
                        caption: translate("Thông tin ưu đãi"),
                    },

                ]}
            />
            {isShowEditModal &&
                <AnhChiEmEditModal
                    hocSinh={hocSinh}
                    onSuccess={handleReload}
                />
            }
        </Box>
    );
};

export default AnhChiEmPage;